// lib/services/api.ts
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { cookieUtils } from '../utils/cookies';
import { checkRateLimit } from '../utils/rateLimit';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
if (!API_BASE_URL && typeof window !== 'undefined') {
    console.error('[api] NEXT_PUBLIC_API_URL is not set. API calls will fail.');
}

const BEARER_KEY_PREFIX = process.env.NEXT_PUBLIC_BEARER_KEY_PREFIX ?? 'portfolio_bearer_';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// ── Axios instance ────────────────────────────────────────────────────────────

const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30_000,
});

// ── Request interceptor ───────────────────────────────────────────────────────

api.interceptors.request.use(
    (config) => {
        const method = config.method?.toLowerCase() ?? '';

        // Rate-limit mutating requests per endpoint to prevent spam
        if (MUTATING_METHODS.has(method)) {
            const key = `${method}:${config.url ?? 'unknown'}`;
            if (!checkRateLimit(key, 10, 60_000)) {
                return Promise.reject(
                    new Error('Too many requests. Please wait before trying again.')
                );
            }
        }

        if (typeof window !== 'undefined') {
            const token = cookieUtils.getToken();
            if (token) {
                config.headers.Authorization = `${BEARER_KEY_PREFIX}${token}`;
            }
        }

        // Add CSRF token header for mutating requests when available
        if (MUTATING_METHODS.has(method) && typeof window !== 'undefined') {
            const csrfToken = document.querySelector<HTMLMetaElement>(
                'meta[name="csrf-token"]'
            )?.content;
            if (csrfToken) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
        }

        // Let the browser set the correct multipart boundary for FormData
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ── Response interceptor ──────────────────────────────────────────────────────

api.interceptors.response.use(
    (response) => response.data,
    (error: AxiosError) => {
        // Never leak raw server error internals to the caller
        const status = error.response?.status;
        if (status === 401) return Promise.reject(new Error('Unauthorized'));
        if (status === 403) return Promise.reject(new Error('Forbidden'));
        if (status === 429) return Promise.reject(new Error('Too many requests'));
        if (status && status >= 500) return Promise.reject(new Error('Server error. Please try again later.'));
        return Promise.reject(error.response?.data || new Error(error.message));
    }
);

export default api;

// ── Generic HTTP helpers ──────────────────────────────────────────────────────

export const apiMethods = {
    get: <T>(url: string, config?: AxiosRequestConfig) =>
        api.get<unknown, T>(url, config),

    post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        api.post<unknown, T>(url, data, config),

    put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        api.put<unknown, T>(url, data, config),

    patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        api.patch<unknown, T>(url, data, config),

    delete: <T>(url: string, config?: AxiosRequestConfig) =>
        api.delete<unknown, T>(url, config),
};
